import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Dropdown } from 'react-bootstrap';
import { BiMessageSquareDetail, BiBell } from 'react-icons/bi';
import { FaCalendarCheck, FaCalendarTimes, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';
import { MdNotifications } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchConversations } from '../../redux/slices/conversationSlice';
import { fetchUserDetails } from '../../redux/userSlice';
import storage from '../../utils/storage';
// import userPlaceholder from '../../assets/svgs/user-placeholder.svg';
import './header.css';
import { fetchNotifications, selectNotifications, selectUnreadNotifications, selectNotificationsLoading, markNotificationAsRead, markAllNotificationsRead } from '../../redux/notificationsSlice';
import { socketActions } from '../../redux/middleware/socketMiddleware';
import moment from 'moment';
// import { CircularProgress } from '@mui/material';

// Helper function for user initials
const getInitials = (name) => {
  if (!name) return "U";
  return name.charAt(0).toUpperCase();
};

// Helper function to get notification icon
const getNotificationIcon = (type, entity_type) => {
  switch (type?.toLowerCase() || entity_type?.toLowerCase()) {
    case 'booking':
    case 'facility booked':
      return <FaCalendarAlt className="notification-type-icon" />;
    case 'booking cancelled':
      return <FaCalendarTimes className="notification-type-icon cancelled" />;
      case 'multiple booking cancelled':
      return <FaCalendarTimes className="notification-type-icon cancelled" />;
    case 'booking confirmed':
      return <FaCalendarCheck className="notification-type-icon confirmed" />;
    case 'payment':
      return <FaMoneyBillWave className="notification-type-icon payment" />;
    default:
      return <MdNotifications className="notification-type-icon" />;
  }
};

// Avatar component
const UserAvatar = ({ src, name, className = "" }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`user-avatar ${className}`}
        onError={(e) => {
          e.target.parentElement.innerHTML = `<div class="user-avatar profile-initial ${className}">${getInitials(name)}</div>`;
        }}
      />
    );
  }
  return (
    <div className={`user-avatar profile-initial ${className}`}>
      {getInitials(name)}
    </div>
  );
};


const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unreadNotifications = useSelector(selectUnreadNotifications);
  const notificationsLoading = useSelector(selectNotificationsLoading);
  const notifications = useSelector(selectNotifications);

  const [localUserName, setLocalUserName] = useState("User");
  const [profilePic, setProfilePic] = useState(null);
  const [userId, setUserId] = useState(null);
  // Use a module-level flag that persists across remounts
  const socketInitAttemptedRef = useRef(false);
  
  // Also use sessionStorage as a backup to track initialization across remounts
  const getSocketInitFlag = () => {
    try {
      return sessionStorage.getItem('socketInitAttempted') === 'true';
    } catch {
      return false;
    }
  };
  
  const setSocketInitFlag = (value) => {
    try {
      sessionStorage.setItem('socketInitAttempted', value ? 'true' : 'false');
    } catch {
      // Ignore if sessionStorage is not available
    }
  };

  // Get user details from Redux store
  const { details, loading: userLoading } = useSelector((state) => state.user || {});
  const userDetail = details?.userDetail || {};

  // Validate image URL before setting
  const validateAndSetProfilePic = (url) => {
    console.log('Validating profile pic URL:', url);

    if (!url) {
      console.log('No URL provided');
      return;
    }

    // Create a new image to test loading
    const img = new Image();
    img.onload = () => {
      console.log('Image loaded successfully:', url);
      setProfilePic(url);
    };
    img.onerror = () => {
      console.log('Image failed to load:', url);
      setProfilePic(null);
    };
    img.src = url;
  };

  // Fetch user details on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching user details...');
        const result = await dispatch(fetchUserDetails()).unwrap();
        console.log('Fetch result:', result);

        if (result?.userDetail) {
          const userData = result.userDetail;
          console.log('User data received:', userData);

          setLocalUserName(userData.firstName || "User");

          if (userData.profilePic) {
            console.log('Found profile pic in user data:', userData.profilePic);
            validateAndSetProfilePic(userData.profilePic);
          }
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };
    fetchData();
  }, [dispatch]);

  // Update local state when userDetail changes
  useEffect(() => {
    console.log('userDetail changed:', userDetail);

    if (userDetail) {
      setLocalUserName(userDetail.firstName || "User");

      if (userDetail.profilePic) {
        console.log('Profile pic from userDetail:', userDetail.profilePic);
        validateAndSetProfilePic(userDetail.profilePic);
      }
    }
  }, [userDetail]);

  const user = {
    name: localUserName,
    image: profilePic
  };

  // Debug: Log profile image updates
  useEffect(() => {
    console.log('Current profile image:', user.image);
  }, [user.image]);

  // Get conversations from Redux store
  const { conversations, loading } = useSelector(state => state.conversations);

  // Get latest 5 conversations
  const latestConversations = conversations.slice(0, 5);

  // Calculate total unread messages
  const unreadMessages = conversations.reduce((total, conv) => total + (conv.unread || 0), 0);

  // Debug: Log unread messages count when it changes
  useEffect(() => {
    console.log('Header: Unread messages count updated:', unreadMessages, {
      conversations: conversations.map(c => ({ id: c.id, name: c.name, unread: c.unread }))
    });
  }, [unreadMessages, conversations]);

  // Get userId from storage
  useEffect(() => {
    const getUserId = async () => {
      try {
        const storedUserId = await storage.getItem('userId');
        setUserId(storedUserId);
      } catch (error) {
        console.error('Error getting userId from storage:', error);
      }
    };
    getUserId();
  }, []);

  // Fetch conversations, user details and notifications on component mount
  useEffect(() => {
    dispatch(fetchConversations());
    dispatch(fetchNotifications({ page: 1, limit: 10 }));
  }, [dispatch]);

  // Connect to socket only once when userId is available
  // Use both ref and sessionStorage to prevent multiple connection attempts across remounts
  useEffect(() => {
    if (!userId) return;
    
    const hasAttempted = socketInitAttemptedRef.current || getSocketInitFlag();
    
    if (!hasAttempted) {
      console.log('Header: Initializing socket connection for userId:', userId);
      dispatch(socketActions.connect(userId));
      socketInitAttemptedRef.current = true;
      setSocketInitFlag(true);
    } else {
      // If we already attempted, just ensure connection (middleware will handle if already connected)
      // Only log this once to avoid spam
      if (!socketInitAttemptedRef.current) {
        console.log('Header: Socket connection already attempted (from session), ensuring connection');
        socketInitAttemptedRef.current = true;
      }
      // Still dispatch to ensure connection, but middleware will skip if already connected
      dispatch(socketActions.connect(userId));
    }
  }, [userId, dispatch]);

  const handleNotificationClick = (notification) => {
    if (notification.status === 'unread') {
      dispatch(markNotificationAsRead(notification._id));
    }
    // Navigate to notifications page
    navigate('/notifications');
  };

  const handleViewAllClick = () => {
    // Mark all notifications as read and navigate
    if (unreadNotifications > 0) {
      dispatch(markAllNotificationsRead());
    }
    navigate('/notifications');
  };

  const handleLogout = async () => {
    try {
      // Disconnect socket before clearing storage
      dispatch(socketActions.disconnect());
      await storage.clear(); // This will clear both cookies and localStorage
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still disconnect socket and navigate even if clear fails
      dispatch(socketActions.disconnect());
      navigate('/');
    }
  };

  // Custom Message Dropdown
  const CustomMessageToggle = React.forwardRef(({ onClick }, ref) => (
    <button
      ref={ref}
      className="header-icon-btn"
      onClick={onClick}
    >
      <BiMessageSquareDetail size={24} />
      {unreadMessages > 0 && (
        <span className="notification-badge">{unreadMessages}</span>
      )}
    </button>
  ));

  // Custom Notification Toggle
  const CustomNotificationToggle = React.forwardRef(({ onClick }, ref) => (
    <button
      ref={ref}
      className="header-icon-btn"
      onClick={onClick}
    >
      <BiBell size={24} />
      {unreadNotifications > 0 && (
        <span className="notification-badge">{unreadNotifications}</span>
      )}
    </button>
  ));

  // Custom Profile Toggle
  const CustomProfileToggle = React.forwardRef(({ onClick }, ref) => (
    <button
      ref={ref}
      className="user-profile-toggle"
      onClick={onClick}
    >
      {profilePic ? (
        <img
          src={profilePic}
          alt={user.name}
          className="user-avatar"
          onError={() => {
            console.log('Image error event triggered');
            setProfilePic(null);
          }}
        />
      ) : (
        <div className="user-avatar profile-initial">
          {localUserName.charAt(0).toUpperCase()}
        </div>
      )}
    </button>
  ));

  return (
    <div className="">
      <Container fluid className="px-4">
        <Row className="align-items-center justify-content-end">
          <Col xs="auto" className="d-flex align-items-center gap-3">
            {/* Messages Dropdown */}
            <Dropdown align="end">
              <Dropdown.Toggle as={CustomMessageToggle} id="messages-dropdown" />
              <Dropdown.Menu className="messages-dropdown">
                <div className="dropdown-header">
                  <h6>Messages</h6>
                  {unreadMessages > 0 && (
                    <span className="message-count">{unreadMessages}</span>
                  )}
                </div>
                <div className="messages-list">
                  {loading ? (
                    <div className="no-messages">
                      <p>Loading conversations...</p>
                    </div>
                  ) : (
                    <>
                      {latestConversations.map(conv => (
                        <Dropdown.Item
                          key={conv.id}
                          className="message-item"
                          onClick={() => navigate('/messages', {
                            state: { preselectUserName: conv.name }
                          })}
                        >
                          <div className="message-avatar">
                            <UserAvatar
                              src={conv.userData?.profilePic}
                              name={conv.name}
                            />
                          </div>
                          <div className="message-content">
                            <div className="message-header">
                              <span className="sender-name">{conv.name}</span>
                              <span className="message-time">{conv.time}</span>
                              {conv.unread > 0 && (
                                <span className="unread-badge">{conv.unread}</span>
                              )}
                            </div>
                            <p className="message-text">
                              {conv.lastMessage || 'No messages yet'}
                              
                            </p>
                          </div>
                        </Dropdown.Item>

                      ))}
                      {!loading && latestConversations.length === 0 && (
                        <div className="no-messages">
                          <p>No conversations yet</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="dropdown-footer">
                  <button className="view-all-link" onClick={() => navigate('/messages')}>View all</button>
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {/* Notifications Dropdown */}
            <Dropdown align="end">
              <Dropdown.Toggle as={CustomNotificationToggle} id="notifications-dropdown" />
              <Dropdown.Menu className="notifications-dropdown">
                <div className="dropdown-header">
                  <h6>Notifications</h6>
                  {unreadNotifications > 0 && (
                    <span className="notification-count">{unreadNotifications}</span>
                  )}
                </div>
                <div className="notifications-list">
                  {notificationsLoading ? (
                    <div className="no-messages"><p>Loading...</p></div>
                  ) : notifications.length === 0 ? (
                    <div className="no-messages"><p>No notifications</p></div>
                  ) : (
                    notifications.slice(0, 5).map(n => (
                      <Dropdown.Item
                        key={n._id}
                        className={`message-item ${n.status === 'unread' ? 'notification-unread' : ''}`}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className="message-avatar">
                          {getNotificationIcon(n.title, n.entity_type)}
                        </div>
                        <div className="message-content">
                          <div className="message-header">
                            <span className="sender-name">{n.title}</span>
                            <span className="message-time">{moment(n.createdAt).format('HH:mm')}</span>
                          </div>
                          <p className="message-text">
                            {n.description}
                            {n.entity_type === 'Booking' && (n.actions?.BookingId || n.actions?.bookingID || n.actions?.BookingID) && (
                              <span className="booking-id-tag">Booking #{n.actions?.BookingId ?? n.actions?.BookingID ?? n.actions?.bookingID}</span>
                            )}
                          </p>
                        </div>
                        {n.status === 'unread' && <div className="notification-dot"></div>}
                      </Dropdown.Item>
                    ))
                  )}
                </div>
                <div className="dropdown-footer">
                  <button className="view-all-link" onClick={handleViewAllClick}>View all</button>
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {/* User Profile Dropdown */}
            <Dropdown align="end">
              <Dropdown.Toggle as={CustomProfileToggle} id="profile-dropdown" />
              <Dropdown.Menu>
                <Dropdown.Item href="/profile">My Profile</Dropdown.Item>
                <Dropdown.Item href="/account-settings">Account Settings</Dropdown.Item>
                <Dropdown.Divider />
                {/* <Dropdown.Item href="/logout">Logout</Dropdown.Item> */}
                <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>

              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Header;
